import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import cloneDeep from 'lodash/cloneDeep';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { TCustomField } from '../../../custom-fields/custom-fields.model';
import { GET_CUSTOM_FIELDS } from '../../../custom-fields/custom-fields.store';
import { GET_WORKSPACE } from '../../../workspace/workspace.store';
import { EWorkspaces } from '../../../workspace/workspaces.enum';
import { SiteTableOverviewService } from '../../site-table-overview/site-table-overview.service';
import { TColumn } from '../../site-table/columns/column.model';
import { generateDefaultOverviewColumns } from '../../site-table/columns/columns';
import { GET_COLUMNS } from '../../site-table/columns/columns.store';
import { TableColumnsService } from '../../site-table/columns/table-columns.service';
import { CustomTableService } from '../../site-table/custom-table/custom-table.service';
import { SiteOptionsService } from '../site-options.service';
import { restoreGroupingAndSorting } from './utils/restore-grouping-and-sorting';

@Injectable({
  providedIn: 'root',
})
export class SiteOptionColumnDropdownService {
  constructor(
    private router: Router,
    private siteOptionsService: SiteOptionsService,
    private siteTableOverviewService: SiteTableOverviewService,
    private customTableService: CustomTableService,
    private tableColumnsService: TableColumnsService,
  ) {}

  resetColumns(resetGroupingAndSorting: boolean = false, workspaceId: string): void {
    const isOverview = this.router.url.search(EWorkspaces.OVERVIEW) !== -1;

    if (!isOverview) {
      this.resetSiteTable(resetGroupingAndSorting, workspaceId);
    } else {
      this.resetOverviewTable(resetGroupingAndSorting);
    }

    logEventInGTAG(EGoogleEventSite.SITE__COLUMNS__RESET, {
      source: EGoogleEventCategory.SITE,
    });
  }

  private resetOverviewTable(resetGroupingAndSorting: boolean): void {
    const backupColumns = cloneDeep(this.tableColumnsService.getColumns());
    const customFields = Object.values(GET_CUSTOM_FIELDS());

    const defaultColumns = generateDefaultOverviewColumns();
    const resettedColumns = this.customTableService.resetColumnData();
    const filteredColumns = this.siteTableOverviewService.filterOverviewColumns(
      defaultColumns,
      resettedColumns,
    );
    const { columns } = this.customTableService.checkColumnsChanged(
      filteredColumns,
      customFields,
      EWorkspaces.OVERVIEW,
    );

    const newColumns = this.tryRestoreOverviewGroupingAndSorting(
      resetGroupingAndSorting,
      columns,
      backupColumns,
    );

    this.tableColumnsService.setColumns(newColumns);
    this.updateTable();
  }

  private tryRestoreOverviewGroupingAndSorting(
    resetGroupingAndSorting: boolean,
    columns: TColumn[],
    backupColumns: TColumn[],
  ): TColumn[] {
    const newColumns = resetGroupingAndSorting
      ? columns
      : restoreGroupingAndSorting(backupColumns, columns);

    return newColumns;
  }

  private resetSiteTable(resetGroupingAndSorting: boolean, workspaceId: string): void {
    const columnsBackup = GET_COLUMNS();
    const customFields = this.getOrderedCustomFields(workspaceId);
    const columns = this.customTableService.generateDefaultSiteColumns(customFields, workspaceId);
    const newColumns = this.tryRestoreTableGroupingAndSorting(
      resetGroupingAndSorting,
      columns,
      columnsBackup,
    );

    this.tableColumnsService.setColumns(newColumns);
    this.updateTable();
  }

  private tryRestoreTableGroupingAndSorting(
    resetGroupingAndSorting: boolean,
    columns: TColumn[],
    columnsBackup: TColumn[],
  ): TColumn[] {
    const newColumns = resetGroupingAndSorting
      ? columns
      : restoreGroupingAndSorting(columnsBackup, columns);

    return newColumns;
  }

  private getOrderedCustomFields(workspaceId: string): TCustomField[] {
    const allCustomFields = GET_CUSTOM_FIELDS();
    const workspace = GET_WORKSPACE(workspaceId);

    if (workspace?.customFields) {
      const ordered: TCustomField[] = [];

      workspace.customFields.forEach((fieldId) => {
        if (allCustomFields[fieldId]) {
          ordered.push(allCustomFields[fieldId]);
        }
      });

      return ordered;
    }

    return Object.values(allCustomFields);
  }

  private updateTable(): void {
    const sortButton = this.siteOptionsService.getSortButton();
    const groupButton = this.siteOptionsService.getGroupButton();
    const table = this.customTableService.getTable();
    table.generateColumns();

    table.tableBody.virtualScroller.disableSmoothScrolling();
    table.updateWidth();
    table.load({ keepScrollPosition: false });
    table.tableBody.virtualScroller.enableSmoothScrolling();

    sortButton.update();
    groupButton.update();
  }
}
